import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { trackFailed } from '../../../../shared-web/track';
import { SESSION_CHANGED_EVENT } from '../../../../shared-web/inbound-door';
import { apiFetch, ApiError, ACCOUNT_CLOSED_EVENT } from './api-client';
import { clearCachedSession, clearLocalAccountData, readCachedSession, writeCachedSession } from './session-storage';
import type { DeleteAccountResult, MeResponse, SessionContextValue, SessionInfo } from './types';

const SessionContext = createContext<SessionContextValue | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  /**
   * Optional bootstrap session injected by the server (e.g., right
   * after onboarding submit when we already minted a token). When
   * provided, takes precedence over the cached value for first paint.
   * The actual auth still comes from the HttpOnly session cookie —
   * this is just a hint for UI rendering speed.
   */
  bootstrapSession?: { actor_id: string; self_name?: string } | null;
}

/**
 * The session the closed screen stands on (2026-10-21 Riley): the close
 * revoked the cookie, so there is no /api/me to read — only the fact that
 * this account is closed. EmberApp renders the closed screen in place of
 * everything else for it.
 */
function closedSessionInfo(prev: SessionInfo | null, where: 'app_open' | 'in_session'): SessionInfo {
  return {
    actor_id: prev?.actor_id || '',
    self_name: prev?.self_name || '',
    kind: 'person',
    session_token: '',
    session_expires_at: '',
    ori_number: '',
    legal_needs_review: false,
    legal_notice_due: false,
    legal_current_version: '',
    deletion_pending: false,
    deletion_purge_at: '',
    account_closed: true,
    account_closed_where: prev?.account_closed ? (prev.account_closed_where || where) : where,
    background_experience: true,
    share: true,
  };
}

const isAccountClosedError = (err: unknown): boolean => err instanceof ApiError && err.status === 403
  && (err.body as { error?: unknown } | null)?.error === 'account_closed';

function toSessionInfo(me: MeResponse): SessionInfo {
  return {
    actor_id: me.actor_id,
    self_name: me.self_name || '',
    kind: me.kind || 'person',
    // Cookie-based auth — there is no client-readable token, but
    // we keep the field on SessionInfo for back-compat with code
    // that displayed the token's expiry. Empty here is fine.
    session_token: '',
    session_expires_at: me.session_expires_at || '',
    ori_number: String(me.ori_number || '').trim(),
    legal_needs_review: me.legal?.needs_review === true,
    legal_notice_due: me.legal?.notice_due === true,
    legal_current_version: String(me.legal?.current_version || '').trim(),
    deletion_pending: me.account_deletion?.pending === true,
    deletion_purge_at: String(me.account_deletion?.purge_at || '').trim(),
    account_closed: me.account_closed === true,
    ...(me.account_closed === true ? { account_closed_where: 'app_open' as const } : {}),
    background_experience: me.privacy?.background_experience !== false,
    share: me.privacy?.share !== false,
  };
}

export function SessionProvider({ children, bootstrapSession }: SessionProviderProps): ReactElement {
  const previewAnonymous = typeof window !== 'undefined' && import.meta.env.DEV
    && new URLSearchParams(window.location.search).get('preview') === 'anonymous';
  const [session, setSession] = useState<SessionInfo | null>(() => {
    if (previewAnonymous) return null;
    // First-paint hint: prefer server-injected bootstrap, fall back
    // to the localStorage UX cache. The real auth check happens in
    // the /api/me effect below.
    if (bootstrapSession?.actor_id) {
      return {
        actor_id: bootstrapSession.actor_id,
        self_name: bootstrapSession.self_name || '',
        kind: 'person',
        session_token: '',
        session_expires_at: '',
        ori_number: '',
        legal_needs_review: false,
        legal_notice_due: false,
        legal_current_version: '',
        deletion_pending: false,
        deletion_purge_at: '',
        account_closed: false,
        background_experience: true,
        share: true,
      };
    }
    const cached = readCachedSession();
    if (!cached?.actor_id) return null;
    return {
      actor_id: cached.actor_id,
      self_name: cached.self_name,
      kind: 'person',
      session_token: '',
      session_expires_at: '',
      ori_number: '',
      legal_needs_review: false,
      legal_notice_due: false,
      legal_current_version: '',
      deletion_pending: false,
      deletion_purge_at: '',
      account_closed: false,
      background_experience: true,
      share: true,
    };
  });
  // The server minted this document knowing the session cookie, so a
  // bootstrap actor is the truth of first paint — not a hint to distrust
  // until /api/me answers. Starting at 'loading' regardless made every
  // status-gated surface treat a signed-in visitor as maybe-anonymous for
  // one round-trip: the guest consent card flashed over /talk?mode=chat and
  // the trial door flashed over /collect for 1–2s (prod 2026-09-04). The
  // /api/me effect below still verifies and downgrades a dead cookie. The
  // localStorage hint stays 'loading': it is a UX cache, not the server.
  const [status, setStatus] = useState<SessionContextValue['status']>(() => (
    !previewAnonymous && bootstrapSession?.actor_id ? 'authenticated' : 'loading'
  ));
  const initialFetched = useRef(false);
  // `refresh` is memoised on [session]; the ref keeps the status it reads
  // current without widening those deps.
  const statusRef = useRef(status);
  statusRef.current = status;

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const me = await apiFetch<MeResponse>('/api/me');
      const next = toSessionInfo(me);
      writeCachedSession({
        session_token: '',
        actor_id: next.actor_id,
        self_name: next.self_name,
      });
      setSession(next);
      setStatus('authenticated');
    } catch (err) {
      // The account was closed under this cookie (the close revoked it): the closed screen, not the sign-in page.
      if (isAccountClosedError(err)) {
        clearCachedSession();
        const where = statusRef.current === 'authenticated' ? 'in_session' : 'app_open';
        setSession((prev) => closedSessionInfo(prev, where));
        setStatus('authenticated');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        // A 401 against a session that was live in this tab is an expiry the
        // person will feel; a cold-boot 401 is just the anonymous state.
        if (statusRef.current === 'authenticated') trackFailed('session', 'expired_mid_session');
        clearCachedSession();
        setSession(null);
        setStatus('anonymous');
        return;
      }
      // What the read died of (2026-09-30): prod wrote 36 of these in 30 days,
      // most from the Facebook in-app browser on its first seconds, and the
      // server log has no /api/me request at that moment — the failure is on
      // the browser's side and this is the only place that can name it.
      const errorDetail = err instanceof ApiError
        ? `HTTP ${err.status}`
        : `${(err as Error)?.name || 'Error'}: ${(err as Error)?.message || ''}`;
      trackFailed('session', 'read_failed', { error_detail: errorDetail.slice(0, 120) });
      // Network error — keep whatever we had, stay loading-ish.
      setStatus(session ? 'authenticated' : 'anonymous');
    }
  }, [session]);

  // setToken is kept for backward compatibility with callers that
  // mint a session client-side. With cookie-based auth the cookie
  // is set by the server; here we just trigger a refresh.
  const setToken = useCallback(async (_token: string): Promise<void> => {
    void _token;
    await refresh();
  }, [refresh]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/api/web-session/logout', { method: 'POST' });
    } catch {
      // best-effort server-side revoke; cookie clear is server's job
    }
    clearCachedSession();
    setSession(null);
    setStatus('anonymous');
  }, []);

  // Log out on the closed screen (2026-10-21 Riley): the server takes the
  // cookie off (the session itself died with the close), and this browser
  // forgets everything it kept for the account — drafts and caches included.
  const logoutClosed = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/api/web-session/logout', { method: 'POST' });
    } catch {
      // best-effort; nothing here is a session any more
    }
    clearLocalAccountData();
    setSession(null);
    setStatus('anonymous');
  }, []);

  // Soft delete: the endpoint schedules the account for deletion
  // (status=pending_deletion, retained 30 days), revokes every session for
  // this actor, then clears the cookie. The cached session goes with it; the
  // page stays as it is until the caller leaves it with a reload (2026-10-30
  // e2e: flipping to anonymous here unmounted the signed-in page under the
  // dialog, so nothing ever said the deletion was scheduled). Rejects on
  // failure so callers can surface a message instead of treating it as done.
  //
  // Getting the account back is a separate, explicit act — restoreAccount
  // below (the restore screen a later sign-in lands on) or replying RESTORE to
  // Ember. Signing in on its own does nothing to the pending deletion.
  const deleteAccount = useCallback(async (): Promise<DeleteAccountResult> => {
    const result = await apiFetch<DeleteAccountResult>('/api/account/delete', { method: 'POST' });
    clearCachedSession();
    return result;
  }, []);

  // Cancel a pending deletion, then re-read /api/me so the app leaves the
  // restore screen on the server's word rather than on an optimistic local
  // flip. Errors propagate: the screen keeps the pending state and says the
  // restore failed, which is the honest render when the account is still
  // scheduled for deletion.
  const restoreAccount = useCallback(async (): Promise<void> => {
    await apiFetch('/api/account/restore', { method: 'POST' });
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (initialFetched.current) return;
    initialFetched.current = true;
    if (previewAnonymous) {
      setSession(null);
      setStatus('anonymous');
      return;
    }
    void refresh();
    // Intentionally only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The sign-in door says the cookie changed under this document (its status
  // poll completed, or /start found the browser already signed in). The full
  // page load that normally follows is what flips this provider, and some
  // in-app webviews never perform it — so re-read /api/me here as well.
  // Any request answered 403 account_closed (api-client.ts): the app was open
  // when Oriscen closed the account — the closed screen now, whatever was on
  // screen (a chat, a call, a room) closes under it.
  useEffect(() => {
    const onClosed = (): void => {
      clearCachedSession();
      setSession((prev) => closedSessionInfo(prev, statusRef.current === 'authenticated' ? 'in_session' : 'app_open'));
      setStatus('authenticated');
    };
    window.addEventListener(ACCOUNT_CLOSED_EVENT, onClosed);
    return () => { window.removeEventListener(ACCOUNT_CLOSED_EVENT, onClosed); };
  }, []);

  useEffect(() => {
    const onChanged = (): void => { void refresh(); };
    window.addEventListener(SESSION_CHANGED_EVENT, onChanged);
    return () => { window.removeEventListener(SESSION_CHANGED_EVENT, onChanged); };
  }, [refresh]);

  const value = useMemo<SessionContextValue>(() => ({
    status,
    session,
    actorId: session?.actor_id || '',
    refresh,
    setToken,
    logout,
    logoutClosed,
    deleteAccount,
    restoreAccount,
  }), [status, session, refresh, setToken, logout, logoutClosed, deleteAccount, restoreAccount]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}
